import { useEffect, useState } from 'react';
import { ErrorBanner, PageHeader, Spinner } from '@/components/ui';
import { PageContainer } from '@/components/layout';
import { MetricGroupRowEditor } from '@/components/admin/MetricGroupRowEditor';
import { CreateMetricGroupCard } from '@/components/admin/CreateMetricGroupCard';
import { metricGroupApi } from '@/services/metric-group-api';
import type { IHomepageMetricGroup } from '@skystone/shared';

export function AdminMetricGroupsPage() {
  const [groups, setGroups] = useState<IHomepageMetricGroup[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    metricGroupApi
      .getAll()
      .then((data) => {
        if (!cancelled) setGroups(data);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load metric groups');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  function applyUpdated(updated: IHomepageMetricGroup) {
    setGroups((prev) =>
      prev?.map((g) => (g.groupKey === updated.groupKey ? updated : g)) ?? prev,
    );
  }

  function appendCreated(created: IHomepageMetricGroup) {
    setGroups((prev) => (prev ? [...prev, created] : [created]));
  }

  return (
    <PageContainer variant="wide" className="space-y-6 p-4">
      <PageHeader
        title="Metric Groups"
        subtitle="Edit what users see on the homepage."
      />

      {error && <ErrorBanner message={error} />}

      {!groups && !error && (
        <div className="flex items-center justify-center py-12"><Spinner size="lg" /></div>
      )}

      {groups && (
        <div className="space-y-4">
          <CreateMetricGroupCard onCreated={appendCreated} />
          {groups.map((group) => (
            <MetricGroupRowEditor
              key={group.groupKey}
              group={group}
              onUpdated={applyUpdated}
            />
          ))}
        </div>
      )}
    </PageContainer>
  );
}
